<template>
    <section v-if="countries && genders && documentTypes && territories">
        <template v-if="!single">
            <div class="is-relative">
                <h3 class="stepper__content__header">{{ $t('PERSONAL_DATA') }}</h3>
            </div>
            <steps :activeStep="1" :steps="2" class="text-xs-center mb-4"></steps>
        </template>
        <div class="content__wrapper">
            <div class="d-flex align-center mb-5">
                <h4 class="stepper__content__title with-description">
                    {{ $t('GIVE_PERSONAL_DATA') }}
                </h4>
                <div class="box-shadow box-shadow--arrow-left description">
                    <template v-if="unlockRequest">
                        {{ $t('UNLOCK_REQUEST_INFO') }}
                    </template>
                    <template v-else>
                        {{ $t('MEDICAL_CARD_INFO') }}
                    </template>
                </div>
            </div>

            <div class=" flex-center flex-row row mt-5 mb-1">
                <div class="log-reg-form--register validation-fields-container" novalidate spellcheck="false">
                    <div class="col-xs-12 mb-3" v-if="serverValidationErrors.length > 0">
                        <p class="validate-danger" v-for="error in serverValidationErrors">
                            <img src="./../../../assets/images/icons/excl-red.svg" /> {{ error }}
                        </p>
                    </div>
                    <div class="col-xs-12 col-sm-6">
                        <div class="mb-4">
                            <input
                                class="text-input -white-input-border"
                                :class="{ invalid: !firstNameIsValid && formSubmitted }"
                                maxlength="50"
                                name="firstName"
                                :placeholder="$t('FIRST_NAME')"
                                v-model="firstName"
                                @keyup="inputKeyUp"
                                required
                                type="text"
                            />
                        </div>
                    </div>

                    <div class="col-xs-12 col-sm-6">
                        <div class="mb-4">
                            <input
                                class="text-input -white-input-border"
                                :class="{ invalid: !lastNameIsValid && formSubmitted }"
                                maxlength="50"
                                name="lastName"
                                :placeholder="$t('LAST_NAME')"
                                v-model="lastName"
                                @keyup="inputKeyUp"
                                required
                                type="text"
                            />
                        </div>
                    </div>

                    <div v-if="!unlockRequest" class="col-xs-12 col-sm-6">
                        <div class="mb-4">
                            <input
                                class="text-input -white-input-border"
                                :class="{ invalid: !secondNameIsValid && formSubmitted }"
                                maxlength="50"
                                name="middleName"
                                :placeholder="$t('SECOND_NAME')"
                                v-model="middleName"
                                @keyup="inputKeyUp"
                                type="text"
                            />
                        </div>
                    </div>

                    <div class="col-xs-12 col-sm-6">
                        <div class="mb-4">
                            <input
                                class="validate-email text-input -white-input-border"
                                name="email"
                                v-model="email"
                                :disabled="emailIsDisabled"
                                :class="{ 'disabled-input': emailIsDisabled }"
                                @keyup="inputKeyUp"
                                :placeholder="$t('EMAIL_ADDRESS')"
                                required
                                type="email"
                            />
                        </div>
                    </div>

                    <div class="col-xs-4 col-sm-2 pl-3 pr-0">
                        <div class="mb-4">
                            <el-select style="height: 50px" v-model="dialingCode">
                                <el-option v-for="item in directionalNumbers" :key="item" :label="item" :value="item" />
                            </el-select>
                        </div>
                    </div>

                    <div class="col-xs-8 col-sm-4 pl-2">
                        <div class="mb-4">
                            <input
                                class="text-input -white-input-border"
                                name="phoneNumber"
                                :placeholder="$t('PHONE_NUMBER')"
                                v-model.trim="phoneNumber"
                                @keyup="inputKeyUp"
                                required
                                type="tel"
                            />
                        </div>
                    </div>

                    <div v-if="!unlockRequest" class="col-xs-12 col-sm-6">
                        <div class="mb-4">
                            <input
                                class="text-input -white-input-border"
                                :class="{ invalid: !familyNameIsValid && formSubmitted }"
                                maxlength="50"
                                name="family_name"
                                :placeholder="$t('MAIDEN_NAME')"
                                v-model="motherMaidenName"
                                @keyup="inputKeyUp"
                                required
                                type="text"
                            />
                        </div>
                    </div>

                    <div v-if="!unlockRequest" class="col-xs-12 col-sm-6">
                        <div class="mb-4">
                            <el-select
                                v-model="countryOfCitizenshipCode"
                                filterable
                                :placeholder="$t('NATIONALITY')"
                                :disabled="nationalityIsDisabled"
                                :filter-method="filterOptions"
                            >
                                <el-option
                                    v-for="item in filteredCountries"
                                    :key="item.id"
                                    :label="`${item.name} (${item.nativeName})`"
                                    :value="item.code"
                                />
                            </el-select>
                        </div>
                    </div>

                    <div v-if="!unlockRequest" class="col-xs-12 col-sm-6">
                        <div class="mb-4">
                            <input
                                class="text-input -white-input-border"
                                :class="{ invalid: !peselIsValid && formSubmitted, 'disabled-input': peselIsDisabled }"
                                name="pesel"
                                placeholder="PESEL"
                                v-model="pesel"
                                @keyup="peselInputKeyUp"
                                :required="countryOfCitizenshipCode === 'PL'"
                                :disabled="peselIsDisabled"
                                type="text"
                            />
                        </div>
                    </div>

                    <div v-if="!unlockRequest" class="col-xs-12 col-sm-6">
                        <div class="mb-4">
                            <el-select
                                v-model="genderName"
                                :class="{ 'invalid-date': !genderIsValid && formSubmitted }"
                                :placeholder="$t('GENDER')"
                                :disabled="genderIsDisabled"
                            >
                                <el-option
                                    v-for="gender in genders"
                                    :key="gender.id"
                                    :label="$t(gender.name)"
                                    :value="gender.name"
                                />
                            </el-select>
                        </div>
                    </div>

                    <div v-if="!unlockRequest" class="col-xs-12 col-sm-6">
                        <div class="mb-4">
                            <el-date-picker
                                id="datePicker"
                                v-model="birthDate"
                                type="date"
                                :class="{ 'invalid-date': !dateIsValid && formSubmitted }"
                                :disabled="dateIsDisabled"
                                :placeholder="$t('BIRTH_DATE')"
                                :picker-options="datePickerOptions"
                            >
                            </el-date-picker>
                            <label for="datePicker" class="d-inline">
                                <img class="date-icon" src="/src/assets/images/icons/ico-calendar.png" />
                            </label>
                            <el-tooltip
                                v-if="!dateIsValid && formSubmitted"
                                class="item tooltip-braster"
                                effect="light"
                                :content="$t('DATEPICKER_INFO')"
                                placement="top"
                            >
                                <i class="el-icon-question"></i>
                            </el-tooltip>
                        </div>
                    </div>

                    <div v-if="!unlockRequest" class="col-xs-6 col-sm-3 pr-1">
                        <div class="mb-4">
                            <el-select
                                v-model="documentIssueCountryCode"
                                :placeholder="$t('DOCUMENT_COUNTRY')"
                                :disabled="documentIsDisabled"
                            >
                                <el-option
                                    v-for="item in countries"
                                    :key="item.id"
                                    :label="`${item.name} (${item.nativeName})`"
                                    :value="item.code"
                                />
                            </el-select>
                        </div>
                    </div>

                    <div v-if="!unlockRequest" class="col-xs-6 col-sm-3 pl-1">
                        <div class="mb-4">
                            <el-select
                                class="document-type"
                                v-model="documentTypeName"
                                :class="{ 'invalid-date': !identificationDocumentIsValid && formSubmitted }"
                                :placeholder="$t('IDENTIFICATION_DOCUMENT')"
                                :disabled="documentIsDisabled"
                            >
                                <el-option
                                    v-for="documentType in documentTypes"
                                    :key="documentType.id"
                                    :label="$t(documentType.name)"
                                    :value="documentType.name"
                                />
                            </el-select>
                        </div>
                    </div>

                    <div v-if="!unlockRequest" class="col-xs-12 col-sm-6">
                        <div class="mb-4">
                            <input
                                class="text-input -white-input-border"
                                :class="{ 'disabled-input': documentIsDisabled }"
                                name="document_number"
                                :placeholder="$t('IDENTIFICATION_DOCUMENT_NUMBER')"
                                v-model.trim="documentNumber"
                                :required="countryOfCitizenshipCode !== 'PL'"
                                :disabled="documentIsDisabled"
                                @keyup="inputKeyUp"
                                type="text"
                            />
                        </div>
                    </div>

                    <div v-if="unlockRequest" class="col-xs-12 col-sm-6">
                        <div class="mb-4">
                            <el-select
                                :class="{ 'invalid-date': !territoryIsValid && formSubmitted }"
                                :placeholder="$t('COUNTRY')"
                                filterable
                                v-model="territoryId"
                            >
                                <el-option
                                    v-for="territory in territories"
                                    :key="territory.id"
                                    :label="territory.name"
                                    :value="territory.id"
                                />
                            </el-select>
                        </div>
                    </div>

                    <div class="col-xs-12" v-if="illegalCharacters.length > 0 && formSubmitted">
                        <p class="validate-danger">
                            {{ $t('ILLEGAL_CHARACTERS', { characters: "'" + illegalCharacters.join("', '") + "'" }) }}
                        </p>
                    </div>

                    <div class="col-xs-12" v-if="!unlockRequest && !peselIsValid && formSubmitted">
                        <p class="validate-danger">{{ $t('PESEL_IS_INVALID') }}</p>
                    </div>

                    <div class="col-xs-12" v-if="!unlockRequest && !genderIsFemale && formSubmitted">
                        <p class="validate-danger">{{ $t('YOU_ARE_MAN') }}</p>
                    </div>

                    <div class="col-xs-12" v-if="!unlockRequest && !identificationDocumentIsValid && formSubmitted">
                        <p class="validate-danger">{{ $t('DOCUMENT_DATA') }}</p>
                    </div>

                    <div class="col-xs-12 text-center">
                        <div v-if="unlockRequest">
                            <label class="checkbox -checkbox-long text-left mt-4">
                                <input
                                    class="checkbox__check-input validate-checkbox"
                                    type="checkbox"
                                    v-model="regulationsAgreement"
                                />
                                <span class="checkbox__check -grey-border-checkbox"></span>
                                <span class="checkbox__text -black-checkbox-text log-reg-form__checkbox-text">
                                    {{ $t('I_ACCEPT') }}
                                    <a class="log-reg-form__link" :href="eSklepUrl + $t('LINK_TERMS')" target="_blank">
                                        {{ $t('PRO_ACCOUNT_RODO_TERMS') }}
                                    </a>
                                </span>
                            </label>
                            <button
                                @click="activateAccount"
                                class="w160 mr-3 app-btn app-btn--small app-btn-primary mt-3"
                            >
                                {{ $t('ACTIVATE_ACCOUNT') }}
                            </button>
                        </div>

                        <div v-else-if="single">
                            <button
                                @click="$emit('cancel')"
                                class="w160 mr-3 app-btn app-btn--small app-btn-default mt-3"
                            >
                                {{ $t('CANCEL') }}
                            </button>
                            <button @click="saveUserData" class="w160 mr-3 app-btn app-btn--small app-btn-primary mt-3">
                                {{ $t('SAVE') }}
                            </button>
                        </div>

                        <div v-else>
                            <button @click="saveUserData" class="w160 mr-3 app-btn app-btn--small app-btn-primary mt-3">
                                {{ $t('NEXT') }}
                            </button>
                        </div>
                    </div>

                    <div class="col-xs-12 col-sm-6">
                        <div class="mb-4">
                            <button
                                id="formBtn"
                                class="buttons-container__button validate-test validate-button hidden"
                            ></button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import { config } from './../../../main';
import { directionalNumbers } from './../../../countries';
import { validate } from './../../../validation';
import Steps from './helpers/steps.vue';
import {
    alphabeticSortByName,
    calculateBirthdayFromPesel,
    calculateGenderFromPesel,
    illegalCharactersInWord,
    validatePesel,
} from '../../../utils';
import { mapActions, mapMutations, mapState } from 'vuex';

export default {
    name: 'survey-step5-q1',
    props: {
        single: Boolean,
        unlockRequest: {
            default: false,
            type: Boolean,
        },
    },
    data() {
        return {
            eSklepUrl: config.eSklepUrl,
            formSubmitted: false,
            firstName: '',
            lastName: '',
            middleName: '',
            birthDate: '',
            email: '',
            dialingCode: '',
            phoneNumber: '',
            pesel: '',
            motherMaidenName: '',
            documentTypeName: '',
            documentNumber: '',
            countryOfCitizenshipCode: '',
            documentIssueCountryCode: '',
            genderName: 'FEMALE',
            territoryId: null,
            userIsFull: false,
            directionalNumbers,
            firstNameIsValid: true,
            secondNameIsValid: true,
            lastNameIsValid: true,
            familyNameIsValid: true,
            regulationsAgreement: false,
            serverValidationErrors: [],
            illegalCharacters: [],
            filteredCountries: null,
        };
    },
    watch: {
        countryOfCitizenshipCode: 'nationalityChanged',
    },
    computed: {
        ...mapState(['user']),
        datePickerOptions() {
            const todayMinus18years = this.$moment().subtract(18, 'years');
            return {
                disabledDate: date => date > todayMinus18years,
            };
        },
        peselIsValid() {
            return this.unlockRequest || this.countryOfCitizenshipCode !== 'PL' || validatePesel(this.pesel);
        },
        genderIsValid() {
            return this.unlockRequest || this.genderIsFemale;
        },
        genderIsFemale() {
            return this.getGenderByName('FEMALE').name === this.genderName;
        },
        dateIsValid() {
            return this.unlockRequest || this.birthDate != null;
        },
        identificationDocumentIsValid() {
            if (this.countryOfCitizenshipCode !== 'PL' && this.documentTypeName == null) {
                return false;
            } else if (
                this.countryOfCitizenshipCode === 'PL' &&
                this.documentTypeName == null &&
                this.documentNumber != null
            ) {
                return true;
            } else if (this.documentNumber != null && this.documentTypeName == null) {
                return false;
            } else if (this.documentNumber == null && this.documentTypeName != null) {
                return false;
            } else {
                return true;
            }
        },
        emailIsDisabled() {
            return !this.unlockRequest;
        },
        dateIsDisabled() {
            return this.userIsFull || validatePesel(this.pesel);
        },
        genderIsDisabled() {
            return this.userIsFull || validatePesel(this.pesel);
        },
        nationalityIsDisabled() {
            return this.userIsFull;
        },
        peselIsDisabled() {
            return (
                (!this.unlockRequest && (this.userIsFull || this.countryOfCitizenshipCode !== 'PL')) ||
                (this.unlockRequest && this.countryOfCitizenshipCode !== 'PL')
            );
        },
        documentIsDisabled() {
            return (
                (!this.unlockRequest && (this.userIsFull || this.countryOfCitizenshipCode === 'PL')) ||
                (this.unlockRequest && this.countryOfCitizenshipCode === 'PL')
            );
        },
        territoryIsValid() {
            return !this.unlockRequest || this.territoryId != null;
        },
    },
    asyncComputed: {
        countries() {
            return this.unlockRequest
                ? []
                : this.$http
                      .get('/user/public/registration/countries')
                      .then(({ data }) => data.sort(alphabeticSortByName));
        },
        genders() {
            return this.unlockRequest ? [] : this.$http.get('/user/api/profile/genders').then(r => r.data);
        },
        documentTypes() {
            return this.unlockRequest ? [] : this.$http.get('/user/api/profile/documentTypes').then(r => r.data);
        },
        territories() {
            return this.unlockRequest
                ? this.$http
                      .get('/user/public/registration/territories')
                      .then(({ data }) => data.sort(alphabeticSortByName))
                : [];
        },
        filteredCountries() {
            this.filteredCountries = this.countries;
        },
    },
    methods: {
        ...mapActions(['fetchUser']),
        ...mapMutations(['setUser']),
        nationalityChanged() {
            if (this.countryOfCitizenshipCode !== 'PL') {
                this.pesel = '';
                if (!this.userIsFull) {
                    this.documentIssueCountryCode = this.countryOfCitizenshipCode;
                }
            } else {
                this.documentIssueCountryCode = '';
                this.documentNumber = '';
                this.documentTypeName = null;
            }
        },
        performValidation() {
            this.checkNamesValidation();
            return (
                validate($('#formBtn')) &&
                this.peselIsValid &&
                this.dateIsValid &&
                this.firstNameIsValid &&
                this.secondNameIsValid &&
                this.lastNameIsValid &&
                this.familyNameIsValid &&
                this.genderIsValid &&
                this.identificationDocumentIsValid &&
                this.territoryIsValid
            );
        },
        inputKeyUp() {
            this.serverValidationErrors = [];
            this.pesel = this.pesel.replace(config.numbersOnlyRegex, '');
            this.phoneNumber = this.phoneNumber.replace(config.numbersOnlyRegex, '');
            if (this.formSubmitted) {
                this.performValidation();
            }
        },
        saveUserData() {
            this.formSubmitted = true;
            if (this.performValidation()) {
                const basicForm = {
                    firstName: this.firstName,
                    lastName: this.lastName,
                    middleName: this.middleName,
                    dialingCode: this.dialingCode,
                    phoneNumber: this.phoneNumber,
                    birthDate: this.$moment(this.birthDate).format('YYYY-MM-DD'),
                    motherMaidenName: this.motherMaidenName,
                };
                const extendedForm = {
                    ...basicForm,
                    countryOfCitizenshipCode: this.countryOfCitizenshipCode,
                    pesel: this.pesel,
                    genderName: this.genderName,
                    documentTypeName: this.documentTypeName,
                    documentIssueCountryCode: this.documentIssueCountryCode,
                    documentNumber: this.documentNumber,
                };
                const form = this.userIsFull ? basicForm : extendedForm;
                this.$http.patch('/user/api/profile/user/', form).then(
                    ({ data }) => {
                        this.$store.commit('STATE_SURVEY', {
                            section5: {
                                active_section: 2,
                            },
                        });
                        this.setUser(data);
                        this.$emit('save');
                    },
                    error => {
                        this.serverValidationErrors = [];
                        error.body.forEach(({ field }) => {
                            if (field === 'pesel') {
                                this.serverValidationErrors.push(this.$t('SERVER_ERROR_PESEL'));
                            } else if (field === 'document_number') {
                                this.serverValidationErrors.push(this.$t('SERVER_ERROR_DOCUMENT_NUMBER'));
                            }
                        });
                    },
                );
            }
        },
        peselInputKeyUp() {
            this.inputKeyUp();
            if (this.peselIsValid) {
                this.birthDate = calculateBirthdayFromPesel(this.pesel);
                this.genderName = this.getGenderByName(calculateGenderFromPesel(this.pesel)).name;
            }
        },
        getGenderByName(name) {
            return this.genders.find(i => i.name === name);
        },
        checkNamesValidation() {
            const firstName = this.firstName;
            const firstNameInvalidCharacters = illegalCharactersInWord(config.nameRegex, firstName);
            this.firstNameIsValid = firstName && firstNameInvalidCharacters.length === 0;

            const secondName = this.middleName;
            const secondNameInvalidCharacters = illegalCharactersInWord(config.nameRegex, secondName);
            this.secondNameIsValid = !secondName || (secondName && secondNameInvalidCharacters.length === 0);

            const lastName = this.lastName;
            const lastNameInvalidCharacters = illegalCharactersInWord(config.nameRegex, lastName);
            this.lastNameIsValid = lastName && lastNameInvalidCharacters.length === 0;

            const familyName = this.motherMaidenName;
            const familyNameInvalidCharacters = illegalCharactersInWord(config.nameRegex, familyName);
            this.familyNameIsValid = (familyName && familyNameInvalidCharacters.length === 0) || this.unlockRequest;

            this.illegalCharacters = [
                ...new Set([
                    ...firstNameInvalidCharacters,
                    ...secondNameInvalidCharacters,
                    ...lastNameInvalidCharacters,
                    ...familyNameInvalidCharacters,
                ]),
            ];
        },
        activateAccount() {
            this.formSubmitted = true;
            if (this.performValidation() && this.regulationsAgreement) {
                const unlockRequest = {
                    firstName: this.firstName,
                    lastName: this.lastName,
                    email: this.email,
                    dialingCode: this.dialingCode,
                    phoneNumber: this.phoneNumber,
                    territoryId: this.territoryId,
                };
                this.$emit('sendUnlockRequest', unlockRequest);
            }
        },
        filterOptions(filterValue) {
            let filteredCountries = this.countries.filter(country => {
                return country.name.toLowerCase().startsWith(filterValue.toLowerCase());
            });

            if (filterValue === null) {
                this.filteredCountries = this.countries;
            } else {
                this.filteredCountries = filteredCountries;
            }
        },
    },
    created() {
        if (!this.unlockRequest) {
            this.fetchUser().then(() => {
                const user = this.user;
                this.userIsFull = user.patient.survey ? !!user.patient.survey.hasFilledData : false;
                this.firstName = user.firstName;
                this.lastName = user.lastName;
                this.middleName = user.patient.middleName || '';
                this.birthDate = user.birthDate;
                this.email = user.email;
                this.dialingCode = user.dialingCode;
                this.phoneNumber = user.phoneNumber || '';
                this.pesel = user.pesel || '';
                this.motherMaidenName = user.patient.motherMaidenName || '';
                this.documentTypeName = user.patient.documentType ? user.patient.documentType.name : '';
                this.documentNumber = user.patient.documentNumber ? user.patient.documentNumber : '';
                this.countryOfCitizenshipCode = user.patient.countryOfCitizenship
                    ? user.patient.countryOfCitizenship.code
                    : 'PL';
                this.documentIssueCountryCode = user.patient.documentIssueCountry
                    ? user.patient.documentIssueCountry.code
                    : '';
            });
        } else {
            this.dialingCode = '+48';
            this.countryOfCitizenshipCode = 'PL';
        }
    },
    components: {
        Steps,
    },
};
</script>

<style scoped>
.tooltip-braster {
    position: absolute;
    right: 0;
    top: 0;
}

.disabled-input,
.disabled-input::placeholder {
    background-color: #f5f7fa;
    border-color: #e4e7ed;
    color: #c0c4cc;
    cursor: not-allowed;
}

.fake-label:hover .custom-input ~ .checkmark.disabled-input:after {
    background: #f1eeea;
}

.invalid {
    border: 2px solid red;
}
</style>



// WEBPACK FOOTER //
// survey-step5-q1.vue?2d38e232